import { useCallback, useEffect, useMemo, useState } from "react";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { Button, Stack, Typography } from "@mui/material";

import PriceHistoryRecordEditForm from "./PriceHistoryRecordEditForm";
import PriceHistoryRecordView from "./PriceHistoryRecordView";
import PriceHistoryRecordViewToggle from "./PriceHistoryRecordViewToggle";
import PriceHistoryRecordVoidConfirmDialog from "./PriceHistoryRecordVoidConfirmDialog";

function PriceHistoryRecordPanel({
  item,
  initialPanelMode = "view",
  productFilterChoices,
  retailerChoices,
  provinceChoices,
  unitChoices,
  formatProductOptionLabel,
  isPublishedProductsLoading,
  isLoadingFilterOptions,
  resolveProvinceForApi,
  onSaveRecord,
  onVoidRecord,
}) {
  const [recordPanelMode, setRecordPanelMode] = useState(initialPanelMode);
  const [isVoidConfirmDialogOpen, setIsVoidConfirmDialogOpen] = useState(false);
  const [isVoiding, setIsVoiding] = useState(false);
  const [isFormSaving, setIsFormSaving] = useState(false);
  const [voidErrorMessage, setVoidErrorMessage] = useState("");

  useEffect(() => {
    setRecordPanelMode(initialPanelMode);
  }, [item?.id, initialPanelMode]);

  useEffect(() => {
    setIsVoidConfirmDialogOpen(false);
    setVoidErrorMessage("");
  }, [item?.id]);

  const handleSaveRecord = async (recordId, formValues) => {
    await onSaveRecord(recordId, formValues);
    setRecordPanelMode("view");
  };

  const voidRecordSummaryLines = useMemo(() => {
    if (!item) return [];
    return [
      { label: "Date", value: item.recordAt },
      { label: "Product", value: item.product },
      { label: "Retailer", value: item.retailer },
      { label: "Province", value: item.province },
      { label: "Price", value: item.price },
      { label: "Unit price", value: item.unitPrice },
      {
        label: "Unit quantity",
        value: item.unitQuantityValue != null ? String(item.unitQuantityValue) : "—",
      },
      { label: "Unit", value: item.unit || "—" },
    ];
  }, [item]);

  const handleOpenVoidConfirmDialog = useCallback(() => {
    setVoidErrorMessage("");
    setIsVoidConfirmDialogOpen(true);
  }, []);

  const handleCloseVoidConfirmDialog = useCallback(() => {
    if (isVoiding) return;
    setIsVoidConfirmDialogOpen(false);
  }, [isVoiding]);

  const handleConfirmVoid = useCallback(async () => {
    if (!item?.id || !onVoidRecord) return;

    setIsVoiding(true);
    setVoidErrorMessage("");

    try {
      await onVoidRecord(item.id);
      setIsVoidConfirmDialogOpen(false);
    } catch (error) {
      setVoidErrorMessage(error?.message || "Failed to delete record.");
      setIsVoidConfirmDialogOpen(false);
    } finally {
      setIsVoiding(false);
    }
  }, [item?.id, onVoidRecord]);

  if (!item) {
    return (
      <Typography variant="body2" color="text.secondary">
        No record selected.
      </Typography>
    );
  }

  const isEditMode = recordPanelMode === "edit";
  const isRecordActionBusy = isVoiding || isFormSaving;

  return (
    <Stack spacing={2} sx={{ minWidth: 0, width: "100%" }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="h6" sx={{ pr: 1 }}>
          {isEditMode ? "Edit Record" : item.product}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          {onVoidRecord ? (
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={<DeleteOutlinedIcon />}
              onClick={handleOpenVoidConfirmDialog}
              disabled={isRecordActionBusy}
            >
              Delete
            </Button>
          ) : null}
          <PriceHistoryRecordViewToggle mode={recordPanelMode} onModeChange={setRecordPanelMode} />
        </Stack>
      </Stack>

      {voidErrorMessage ? (
        <Typography variant="body2" color="error">
          {voidErrorMessage}
        </Typography>
      ) : null}

      {isEditMode ? (
        <PriceHistoryRecordEditForm
          item={item}
          productFilterChoices={productFilterChoices}
          retailerChoices={retailerChoices}
          provinceChoices={provinceChoices}
          unitChoices={unitChoices}
          formatProductOptionLabel={formatProductOptionLabel}
          isPublishedProductsLoading={isPublishedProductsLoading}
          isLoadingFilterOptions={isLoadingFilterOptions}
          resolveProvinceForApi={resolveProvinceForApi}
          onSaveRecord={handleSaveRecord}
          onCancel={() => setRecordPanelMode("view")}
          onSavingChange={setIsFormSaving}
        />
      ) : (
        <PriceHistoryRecordView item={item} />
      )}

      <PriceHistoryRecordVoidConfirmDialog
        open={isVoidConfirmDialogOpen}
        summaryLines={voidRecordSummaryLines}
        isVoiding={isVoiding}
        onCancel={handleCloseVoidConfirmDialog}
        onConfirm={handleConfirmVoid}
      />
    </Stack>
  );
}

export default PriceHistoryRecordPanel;
